import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ActionSelector from './ActionSelector';
import FrameworkActionArgumentFields from './FrameworkActionArgumentFields';
import { BUTTON_LABEL as CONTROLLER_BUTTON_LABEL } from './controllerConstants';
import { BUTTON_LABEL as DRIVEBASE_BUTTON_LABEL } from '../Drivebase/drivebasePanelConstants';
import { translateControllerInputButton, translateDrivebaseInputButton } from './inputButtonI18n';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { useResolvedMappingsReady } from '../../../hooks/editor/usePythonTreeSitterReady';
import type { FrameworkAction, FrameworkArgument } from '../../../framework/framework.d';
import { getEnumOptions } from '../../../framework/framework-enums';
import { getInputArgNameForAxisAction } from '../../../framework/framework-axis-actions';
import {
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  isTupleArg,
  readTupleMemberValue,
  sequenceTupleArgValueFromArgValues,
  sequenceTupleRowsToArgValues,
  tupleMemberSchemaDefault,
  tupleMemberValueKey,
  tupleValueFromArgValues,
} from '../../../utils/tupleArg';
import { scalarFrameworkArgIsValid, sequenceTupleRowsAreValid } from '../../../utils/sequenceTupleValidation';
import { isUnsupportedSequenceTupleLiteral } from '../pythonArgParser';
import { useTranslation } from 'react-i18next';
import {
  coerceFrameworkArgValuePreservingExpressions,
  frameworkArgValueToFormString,
  isFrameworkArgOptionalNoneDefault,
  isFrameworkBoolArgType,
  isFrameworkNumberArgType,
  shouldPreserveRawFrameworkBoolArgument,
  shouldPreserveRawFrameworkElectronicArgument,
} from '../../../utils/frameworkArgTypes';
import { collectElectronicVariableNamesForClass, isFrameworkElectronicClassArgType } from '../../../utils/frameworkClassNameSets';
import {
  buildActionLabel,
  clearRawActionArgValue,
  copyRawActionArgValues,
  findActionDefForLabel,
  findActionItemForLabel,
  getRawActionArgOrder,
  getRawActionArgValue,
  isHiddenRuntimeActionArg,
  parseActionLabel,
  removeActionArgValues,
  setRawActionArgOrder,
  setRawActionArgValue,
  updateRawActionArgValue,
  withHiddenRuntimeActionArgs,
} from '../../CodeEditor/ConfigurationTabs/driveModeAutonomousActionUtils';
import { getLocalizedFrameworkArgLabel, getLocalizedFrameworkUnit } from '../../../utils/frameworkI18n';
import { useAllAvailableActions } from '../../../hooks/editor/useAllAvailableActions';
import { excludeGroupOnlyActions } from '../../CodeEditor/ConfigurationTabs/hardwareParser';
import { captureFocusedElement, restoreFocusToElement, type FocusableElement } from '../../../utils/focusRestore';
import { inferSingleReceiverVarName, type SaveButtonBindingsResult } from './controllerCodeUtils';
import type { InputBinding, InputBindingAction, InputBindingDevice, SingleActionInputBinding } from '../../../utils/inputBindings';
import {
  canUseVariableInputBinding,
  dedupeInputBindingsBySignature,
  getSupportedInputBindingsForButtonEditor,
  INPUT_BINDING_DEVICE,
  INPUT_BINDING_KIND,
  inputBindingSignature,
  withInputBindingId,
} from '../../../utils/inputBindings';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import { resolveAutoZeroSensorAction, withApplicableAutoZeroSensorArgs } from '../../../utils/autoZeroSensorActionArgs';

const { DRIVEBASE } = INPUT_BINDING_DEVICE;

const isFollowPathAction = (name: string | undefined): boolean => name === 'follow_path_action';
const FRAMEWORK_ENUM_OPTIONS = getEnumOptions();
const PYTHON_IDENTIFIER_RE = /^[A-Za-z_][\w]*$/;

/**
 * Blur Monaco's textarea if it currently holds focus.
 * Prevents the browser warning when a MUI Modal/Dialog sets aria-hidden on #root
 * while the editor textarea is still the active element.
 */
function blurMonacoEditorIfFocused(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  if (!active.classList.contains('inputarea') && !active.closest('.monaco-editor')) return;
  active.blur();
}

type EditableBindingKind = SingleActionInputBinding['kind'] | typeof INPUT_BINDING_KIND.TOGGLE;

interface BindingDraftProvenance {
  receiverVarName?: string;
  sourceLineNumber?: number;
}

interface SingleActionBindingDraft extends BindingDraftProvenance {
  draftId: string;
  kind: SingleActionInputBinding['kind'];
  actionLabel: string;
  argValues: Record<string, string>;
}

interface ToggleBindingDraft extends BindingDraftProvenance {
  draftId: string;
  kind: typeof INPUT_BINDING_KIND.TOGGLE;
  onActionLabel: string;
  onArgValues: Record<string, string>;
  offActionLabel: string;
  offArgValues: Record<string, string>;
}

type EditableBindingDraft = SingleActionBindingDraft | ToggleBindingDraft;
type ArgHydrationMode = 'semantic' | 'display';
type ButtonMappingSaveResult = boolean | SaveButtonBindingsResult;

interface ButtonMappingDialogProps {
  open: boolean;
  device: InputBindingDevice;
  onClose: () => void;
  onSave: (button: string, bindings: InputBinding[]) => ButtonMappingSaveResult;
  initialButton?: string;
  /** When false, Update stays disabled (Tree-sitter not ready). Cancel remains available.
   * When omitted, falls back to live Tree-sitter readiness. */
  mappingsReady?: boolean;
}

function buttonMappingSaveSucceeded(result: ButtonMappingSaveResult): boolean {
  return typeof result === 'boolean' ? result : result.ok;
}

function buttonMappingSaveFailureReason(result: ButtonMappingSaveResult): string | null {
  return typeof result === 'boolean' || result.ok ? null : result.reason;
}

function actionToLabel(bindingAction: InputBindingAction): string {
  const actionName = String(bindingAction.action?.name || '').trim();
  if (!actionName) return '';
  const parts: string[] = [];
  if (bindingAction.parentName) parts.push(bindingAction.parentName);
  if (bindingAction.mechanismName) parts.push(bindingAction.mechanismName);
  parts.push(actionName);
  return buildActionLabel(parts);
}

function argsToValues(args: FrameworkArgument[] | undefined): Record<string, string> {
  const values: Record<string, string> = {};
  const argOrder: string[] = [];
  (args || []).forEach((arg) => {
    if (arg.name) argOrder.push(arg.name);
    if (isSequenceTupleArg(arg)) {
      if (Array.isArray(arg.value)) {
        Object.assign(values, sequenceTupleRowsToArgValues(arg, arg.value));
      } else if (arg.value != null && isUnsupportedSequenceTupleLiteral(String(arg.value), arg.members ?? [])) {
        // Preserve an unparseable / wrong-shape expression so it renders as unsupported code, not lost.
        values[arg.name] = String(arg.value).trim();
      }
      return;
    }
    if (isTupleArg(arg)) {
      if (arg.rawSourceExpression) {
        values[arg.name] = arg.rawSourceExpression;
        setRawActionArgValue(values, arg.name, arg.rawSourceExpression);
      }
      (arg.members || []).forEach((member, idx) => {
        const stored = readTupleMemberValue(arg, idx, member.name);
        const resolved = stored !== undefined && stored !== null ? stored : tupleMemberSchemaDefault(arg, idx, member as FrameworkArgument);
        values[tupleMemberValueKey(arg.name, member.name)] =
          resolved === undefined || resolved === null ? '' : frameworkArgValueToFormString(member, resolved);
      });
      return;
    }
    values[arg.name] = frameworkArgValueToFormString(arg, arg.value);
    if (arg.rawSourceExpression) {
      if (
        (isFrameworkBoolArgType(arg.type || '') && shouldPreserveRawFrameworkBoolArgument(arg, arg.value, arg.rawSourceExpression)) ||
        (isFrameworkElectronicClassArgType(arg.type) && shouldPreserveRawFrameworkElectronicArgument(arg, arg.value, arg.rawSourceExpression))
      ) {
        values[arg.name] = String(arg.rawSourceExpression).trim();
      }
      setRawActionArgValue(values, arg.name, arg.rawSourceExpression);
    }
  });
  setRawActionArgOrder(values, argOrder);
  return values;
}

function createEmptyPressedDraft(draftId: string, provenance: BindingDraftProvenance = {}): EditableBindingDraft {
  return { draftId, kind: INPUT_BINDING_KIND.PRESSED, actionLabel: '', argValues: {}, ...provenance };
}

function createEmptyToggleDraft(draftId: string, provenance: BindingDraftProvenance = {}): ToggleBindingDraft {
  return { draftId, kind: INPUT_BINDING_KIND.TOGGLE, onActionLabel: '', onArgValues: {}, offActionLabel: '', offArgValues: {}, ...provenance };
}

function normalizedDraftSnapshot(drafts: EditableBindingDraft[]): string {
  return JSON.stringify(
    drafts.map((draft) =>
      draft.kind === INPUT_BINDING_KIND.TOGGLE
        ? {
            kind: draft.kind,
            onActionLabel: draft.onActionLabel,
            onArgValues: Object.fromEntries(Object.entries(draft.onArgValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
            offActionLabel: draft.offActionLabel,
            offArgValues: Object.fromEntries(Object.entries(draft.offArgValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
          }
        : {
            kind: draft.kind,
            actionLabel: draft.actionLabel,
            argValues: Object.fromEntries(Object.entries(draft.argValues).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))),
          }
    )
  );
}

function canonicalBindingSnapshot(bindings: InputBinding[]): string {
  return JSON.stringify(bindings.map(inputBindingSignature).sort());
}

const ButtonMappingDialog: React.FC<ButtonMappingDialogProps> = ({ open, device, onClose, onSave, initialButton, mappingsReady }) => {
  const { t } = useTranslation();
  const ready = useResolvedMappingsReady(mappingsReady);
  const buttonLabelSource = useMemo(() => (device === DRIVEBASE ? DRIVEBASE_BUTTON_LABEL : CONTROLLER_BUTTON_LABEL), [device]);
  const defaultButtonKeys = useMemo(() => Object.keys(buttonLabelSource), [buttonLabelSource]);
  const labelForButtonOption = useCallback(
    (opt: string) => (device === DRIVEBASE ? translateDrivebaseInputButton(opt, t) : translateControllerInputButton(opt, t)),
    [device, t]
  );
  const [button, setButton] = useState<string>(() => initialButton || defaultButtonKeys[0] || '');
  const [drafts, setDrafts] = useState<EditableBindingDraft[]>([]);
  const [initialEffectiveSnapshot, setInitialEffectiveSnapshot] = useState<string>('[]');
  const [pendingButton, setPendingButton] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const allAvailableActions = useAllAvailableActions();
  const globalActionList = useMemo(() => excludeGroupOnlyActions(allAvailableActions), [allAvailableActions]);
  const globalAxisActionList = useAppSelector((state) => state.editor.axisActionList);
  const globalInputBindingList = useAppSelector((state) => state.editor.inputBindingList);
  const project = useAppSelector((state) => state.project.loadedProject);
  const programCode = project?.program?.code ?? '';
  const inputBindingListRef = useRef<InputBinding[]>(globalInputBindingList);
  const draftIdCounterRef = useRef(0);
  const pathOptions = useMemo(
    () => ((project?.paths || []) as Array<{ id: string; name: string }>).map(({ id, name }) => ({ id, name })),
    [project?.paths]
  );
  const focusTriggerRef = useRef<FocusableElement | null>(null);
  const wasOpenRef = useRef(false);
  const bindingKindLabels = useMemo<Record<EditableBindingKind, string>>(
    () => ({
      [INPUT_BINDING_KIND.PRESSED]: t('controller.buttonMapping.triggerPressed', 'Pressed'),
      [INPUT_BINDING_KIND.RELEASED]: t('controller.buttonMapping.triggerReleased', 'Released'),
      [INPUT_BINDING_KIND.VARIABLE]: t('controller.buttonMapping.triggerVariable', 'Variable'),
      [INPUT_BINDING_KIND.TOGGLE]: t('controller.buttonMapping.triggerToggle', 'Toggle'),
    }),
    [t]
  );
  const dialogTitle = t('controller.buttonMapping.dialog.title', {
    button: labelForButtonOption(button) || button,
    defaultValue: 'Edit {{button}} mappings',
  });

  useEffect(() => {
    inputBindingListRef.current = globalInputBindingList;
  }, [globalInputBindingList]);

  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) {
      focusTriggerRef.current = captureFocusedElement();
    }
    wasOpenRef.current = open;
    if (!open) return;
    blurMonacoEditorIfFocused();
  }, [open]);

  const handleDialogExited = useCallback(() => {
    wasOpenRef.current = false;
    const trigger = focusTriggerRef.current;
    focusTriggerRef.current = null;
    restoreFocusToElement(trigger);
  }, []);

  const canUseVariable = useCallback((btn: string) => canUseVariableInputBinding(device, btn), [device]);

  const nextDraftId = useCallback((): string => {
    draftIdCounterRef.current += 1;
    return `binding-draft-${draftIdCounterRef.current}`;
  }, []);

  const bindingToDraft = useCallback(
    (binding: InputBinding): EditableBindingDraft => {
      if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
        return {
          draftId: nextDraftId(),
          kind: INPUT_BINDING_KIND.TOGGLE,
          receiverVarName: binding.receiverVarName,
          sourceLineNumber: binding.sourceLineNumber,
          onActionLabel: actionToLabel(binding.onAction),
          onArgValues: argsToValues(binding.onAction.action?.arguments),
          offActionLabel: actionToLabel(binding.offAction),
          offArgValues: argsToValues(binding.offAction.action?.arguments),
        };
      }
      if (binding.kind === INPUT_BINDING_KIND.UNSUPPORTED) {
        return createEmptyPressedDraft(nextDraftId(), {
          receiverVarName: binding.receiverVarName,
          sourceLineNumber: binding.sourceLineNumber,
        });
      }
      return {
        draftId: nextDraftId(),
        kind: binding.kind,
        receiverVarName: binding.receiverVarName,
        sourceLineNumber: binding.sourceLineNumber,
        actionLabel: actionToLabel(binding.action),
        argValues: argsToValues(binding.action.action?.arguments),
      };
    },
    [nextDraftId]
  );

  const getEditableBindingsForButton = useCallback(
    (btn: string): InputBinding[] => {
      return dedupeInputBindingsBySignature(getSupportedInputBindingsForButtonEditor(inputBindingListRef.current, device, btn));
    },
    [device]
  );

  const createDraftsForButton = useCallback(
    (btn: string): EditableBindingDraft[] => {
      const nextDrafts = getEditableBindingsForButton(btn).map(bindingToDraft);
      return nextDrafts.length > 0 ? nextDrafts : [createEmptyPressedDraft(nextDraftId())];
    },
    [bindingToDraft, getEditableBindingsForButton, nextDraftId]
  );

  const buttonOptions = useMemo(() => {
    const set = new Set<string>(defaultButtonKeys);
    if (button && !set.has(button)) set.add(button);
    if (initialButton && !set.has(initialButton)) set.add(initialButton);
    return Array.from(set);
  }, [button, defaultButtonKeys, initialButton]);

  const actionDefForLabel = useCallback(
    (kind: EditableBindingKind, actionLabel: string): FrameworkAction | null => {
      const actionDef = findActionDefForLabel(kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList || [] : allAvailableActions, actionLabel);
      if (!actionDef) return null;
      if (kind !== INPUT_BINDING_KIND.VARIABLE) return actionDef;
      const inputArgName = getInputArgNameForAxisAction(actionDef.name);
      return {
        ...actionDef,
        arguments: (actionDef.arguments || []).filter((arg) => arg.name !== inputArgName),
      };
    },
    [allAvailableActions, globalAxisActionList]
  );

  const actionItemForLabel = useCallback(
    (kind: EditableBindingKind, actionLabel: string) =>
      findActionItemForLabel(kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList || [] : allAvailableActions, actionLabel),
    [allAvailableActions, globalAxisActionList]
  );

  const actionDefForLabelAndValues = useCallback(
    (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>): FrameworkAction | null =>
      withResolvedMotorVelocityLimits(actionDefForLabel(kind, actionLabel), argValues),
    [actionDefForLabel]
  );

  const runtimeActionDefForLabel = useCallback(
    (kind: EditableBindingKind, actionLabel: string): FrameworkAction | null => withHiddenRuntimeActionArgs(actionDefForLabel(kind, actionLabel)),
    [actionDefForLabel]
  );

  /**
   * Sensor-dependent actions serialize only the args applicable to the selected sensor. Both the
   * schema and the values must drop the rest, otherwise they are written out with empty values, or
   * survive as unknown kwargs, instead of letting the framework apply its own defaults.
   */
  const serializableActionForSave = useCallback(
    (
      kind: EditableBindingKind,
      actionLabel: string,
      argValues: Record<string, string>
    ): { actionDef: FrameworkAction | null; argValues: Record<string, string> } => {
      const actionDef = runtimeActionDefForLabel(kind, actionLabel);
      const resolution = resolveAutoZeroSensorAction(actionDef, argValues, programCode);
      if (!resolution) return { actionDef, argValues };
      return { actionDef: resolution.actionDef, argValues: removeActionArgValues(argValues, resolution.inapplicableArgumentNames) };
    },
    [programCode, runtimeActionDefForLabel]
  );

  /**
   * Mirrors the argument set the fields render, so sensor-specific args dropped as inapplicable are
   * not validated. Kept separate from the rendered def, which must stay unnarrowed so switching the
   * selected sensor can still seed the newly applicable arguments.
   */
  const validatedActionDefForLabelAndValues = useCallback(
    (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>): FrameworkAction | null | undefined =>
      withApplicableAutoZeroSensorArgs(actionDefForLabelAndValues(kind, actionLabel, argValues), argValues, { programCode }),
    [actionDefForLabelAndValues, programCode]
  );

  const argValuesForAction = useCallback(
    (actionDef: FrameworkAction | null, mode: ArgHydrationMode): Record<string, string> => {
      if (!actionDef?.arguments || actionDef.arguments.length === 0) return {};
      const values: Record<string, string> = {};

      const seedScalar = (arg: FrameworkArgument, key: string) => {
        const defaultVal = arg.defaultValue ?? arg.value;
        if (defaultVal !== undefined && defaultVal !== null && String(defaultVal) !== '') {
          values[key] = frameworkArgValueToFormString(arg, defaultVal);
          return;
        }
        if (mode === 'semantic') return;
        const enumOpts = isFrameworkNumberArgType(arg.type || '') ? undefined : FRAMEWORK_ENUM_OPTIONS[arg.type || ''];
        if (isFollowPathAction(actionDef.name) && arg.name === 'path' && pathOptions.length > 0) {
          values[key] = pathOptions[0].name;
        } else if (isFrameworkElectronicClassArgType(arg.type)) {
          const electronicOptions = collectElectronicVariableNamesForClass(programCode, arg.type || '');
          values[key] = electronicOptions[0] ?? '';
        } else if (enumOpts && enumOpts.length > 0) {
          values[key] = enumOpts[0].value;
        } else {
          values[key] = '';
        }
      };

      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // No default rows: a sequence-of-tuples starts empty and rows are user-created
          // (or hydrated from existing code). Nothing to seed here.
          return;
        }
        if (isTupleArg(frameworkArg)) {
          (frameworkArg.members || []).forEach((member, idx) => {
            const m = member as FrameworkArgument;
            const key = tupleMemberValueKey(frameworkArg.name, m.name);
            const fromSchema = tupleMemberSchemaDefault(frameworkArg, idx, m);
            if (fromSchema !== undefined && fromSchema !== null && String(fromSchema) !== '') {
              values[key] = frameworkArgValueToFormString(m, fromSchema);
              return;
            }
            seedScalar(m, key);
          });
          return;
        }
        if (isHiddenRuntimeActionArg(frameworkArg)) return;
        seedScalar(frameworkArg, frameworkArg.name);
      });
      return values;
    },
    [pathOptions, programCode]
  );

  const syncArgValuesForAction = useCallback(
    (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>, mode: ArgHydrationMode): Record<string, string> => {
      if (!actionLabel) return {};
      const actionDef = runtimeActionDefForLabel(kind, actionLabel);
      if (!actionDef?.arguments || actionDef.arguments.length === 0) {
        return {};
      }
      const defaults = argValuesForAction(actionDef, mode);
      const nextValues: Record<string, string> = {};

      const copiedValueKeys: Array<string | { sourceKey: string; targetKey: string }> = [];
      const copyScalar = (arg: FrameworkArgument, key: string, fallbackKey?: string, options?: { skipBlankDefault?: boolean }) => {
        const hasNamedValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, key);
        const hasFallbackValue = fallbackKey ? Object.prototype.hasOwnProperty.call(argValues ?? {}, fallbackKey) : false;
        let sourceKey = key;
        let current = hasNamedValue ? argValues[key] : undefined;
        if (!hasNamedValue && hasFallbackValue && fallbackKey) {
          sourceKey = fallbackKey;
          current = argValues[fallbackKey];
        }
        const rawSourceValue = getRawActionArgValue(argValues, sourceKey);
        if (
          mode === 'display' &&
          isFollowPathAction(actionDef.name) &&
          arg.name === 'path' &&
          current &&
          !rawSourceValue &&
          !pathOptions.some((path) => path.name === current)
        ) {
          nextValues[key] = '';
          return;
        }
        const nextValue = current ?? defaults[key] ?? '';
        if (options?.skipBlankDefault && rawSourceValue === undefined && String(nextValue).trim() === '') {
          return;
        }
        nextValues[key] = nextValue;
        if (rawSourceValue !== undefined) setRawActionArgValue(nextValues, key, rawSourceValue);
        copiedValueKeys.push(sourceKey === key ? key : { sourceKey, targetKey: key });
      };

      actionDef.arguments.forEach((arg, index) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // Carry the indexed row keys (`<arg>.<row>.<member>`) and any preserved
          // unsupported raw value (`<arg>`) through untouched.
          for (const [key, value] of Object.entries(argValues)) {
            if (key === frameworkArg.name || key.startsWith(`${frameworkArg.name}.`)) {
              nextValues[key] = value;
              copiedValueKeys.push(key);
            }
          }
          return;
        }
        if (isTupleArg(frameworkArg)) {
          const tupleRawSourceValue = getRawActionArgValue(argValues, frameworkArg.name);
          if (tupleRawSourceValue !== undefined) {
            nextValues[frameworkArg.name] = tupleRawSourceValue;
            copiedValueKeys.push(frameworkArg.name);
          }
          (frameworkArg.members || []).forEach((member) => {
            const key = tupleMemberValueKey(frameworkArg.name, member.name);
            copyScalar(member as FrameworkArgument, key);
          });
          return;
        }
        copyScalar(frameworkArg, frameworkArg.name, `arg${index}`, {
          skipBlankDefault: isHiddenRuntimeActionArg(frameworkArg),
        });
      });
      const unknownValueKeys: string[] = [];
      for (const [key, value] of Object.entries(argValues ?? {})) {
        if (!PYTHON_IDENTIFIER_RE.test(key) || copiedValueKeys.includes(key) || /^arg\d+$/.test(key)) continue;
        nextValues[key] = value;
        unknownValueKeys.push(key);
        const rawValue = getRawActionArgValue(argValues, key);
        if (rawValue !== undefined) setRawActionArgValue(nextValues, key, rawValue);
      }
      copyRawActionArgValues(argValues, nextValues, [...copiedValueKeys, ...unknownValueKeys]);
      return nextValues;
    },
    [argValuesForAction, pathOptions, runtimeActionDefForLabel]
  );

  const syncArgValues = useCallback(
    (draft: EditableBindingDraft, mode: ArgHydrationMode = 'display'): EditableBindingDraft => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return {
          ...draft,
          onArgValues: syncArgValuesForAction(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel, draft.onArgValues, mode),
          offArgValues: syncArgValuesForAction(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel, draft.offArgValues, mode),
        };
      }
      return {
        ...draft,
        argValues: syncArgValuesForAction(draft.kind, draft.actionLabel, draft.argValues, mode),
      };
    },
    [syncArgValuesForAction]
  );

  const allowedKinds = useMemo<EditableBindingKind[]>(
    () =>
      canUseVariable(button)
        ? [INPUT_BINDING_KIND.PRESSED, INPUT_BINDING_KIND.RELEASED, INPUT_BINDING_KIND.VARIABLE, INPUT_BINDING_KIND.TOGGLE]
        : [INPUT_BINDING_KIND.PRESSED, INPUT_BINDING_KIND.RELEASED, INPUT_BINDING_KIND.TOGGLE],
    [button, canUseVariable]
  );

  const updateDraft = (draftId: string, updater: (draft: EditableBindingDraft) => EditableBindingDraft) => {
    setDrafts((prev) => prev.map((draft) => (draft.draftId === draftId ? updater(draft) : draft)));
  };

  const handleKindChange = (draftId: string, nextKind: EditableBindingKind) => {
    updateDraft(draftId, (draft) => {
      const provenance = { receiverVarName: draft.receiverVarName, sourceLineNumber: draft.sourceLineNumber };
      if (nextKind === INPUT_BINDING_KIND.TOGGLE) return createEmptyToggleDraft(draft.draftId, provenance);
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return { draftId: draft.draftId, kind: nextKind, actionLabel: '', argValues: {}, ...provenance };
      }
      const changedVariableBoundary = draft.kind === INPUT_BINDING_KIND.VARIABLE || nextKind === INPUT_BINDING_KIND.VARIABLE;
      return {
        ...draft,
        kind: nextKind,
        actionLabel: changedVariableBoundary ? '' : draft.actionLabel,
        argValues: changedVariableBoundary ? {} : draft.argValues,
      };
    });
  };

  // Seed a newly selected action with one editable row per sequence-of-tuples argument so the
  // user doesn't have to click "Add" to get started. Rows can still be added/removed afterward.
  const initialArgValuesForLabel = useCallback(
    (kind: EditableBindingKind, label: string): Record<string, string> => {
      const actionDef = actionDefForLabel(kind, label);
      const seeded: Record<string, string> = {};
      (actionDef?.arguments || []).forEach((arg) => {
        if (isSequenceTupleArg(arg as FrameworkArgument)) Object.assign(seeded, defaultSequenceTupleRowArgValues(arg as FrameworkArgument));
      });
      return seeded;
    },
    [actionDefForLabel]
  );

  const handleActionChange = (draftId: string, nextLabel: string, slot?: 'on' | 'off') => {
    updateDraft(draftId, (draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        const seeded = initialArgValuesForLabel(INPUT_BINDING_KIND.TOGGLE, nextLabel);
        const nextDraft =
          slot === 'off'
            ? { ...draft, offActionLabel: nextLabel, offArgValues: seeded }
            : { ...draft, onActionLabel: nextLabel, onArgValues: seeded };
        return syncArgValues(nextDraft, 'display');
      }
      const nextDraft = { ...draft, actionLabel: nextLabel, argValues: initialArgValuesForLabel(draft.kind, nextLabel) };
      return syncArgValues(nextDraft, 'display');
    });
  };

  const handleAddDraft = () => {
    setDrafts((prev) => [
      ...prev,
      createEmptyPressedDraft(nextDraftId(), {
        receiverVarName: inferSingleReceiverVarName(prev) ?? undefined,
      }),
    ]);
  };

  const handleRemoveDraft = (draftId: string) => {
    setDrafts((prev) => prev.filter((draft) => draft.draftId !== draftId));
  };

  const updateArgValuesForAction = (actionDef: FrameworkAction | null, argValues: Record<string, string>, name: string, value: string) => {
    const next = updateRawActionArgValue(argValues, name, value);
    const tupleArg = (actionDef?.arguments || []).find((arg) =>
      isTupleArg(arg as FrameworkArgument)
        ? (arg as FrameworkArgument).members?.some((member) => tupleMemberValueKey(arg.name, member.name) === name)
        : false
    ) as FrameworkArgument | undefined;
    if (tupleArg) {
      delete next[tupleArg.name];
      clearRawActionArgValue(next, tupleArg.name);
    }
    return next;
  };

  const buildBindingAction = (
    actionLabel: string,
    actionDef: FrameworkAction | null,
    actionItem: ReturnType<typeof findActionItemForLabel>,
    argValues: Record<string, string>
  ): InputBindingAction => {
    const { parentName, mechanismName, actionName } = parseActionLabel(actionLabel);
    const actionArgs = actionDef?.arguments;
    type ActionArgBuildEntry = {
      targetKey: string;
      sourceKeys: string[];
      arg: FrameworkArgument;
    };

    const orderActionArgEntries = (entries: ActionArgBuildEntry[]): FrameworkArgument[] => {
      const entriesBySourceKey = new Map<string, ActionArgBuildEntry>();
      entries.forEach((entry) => {
        entry.sourceKeys.forEach((sourceKey) => {
          if (sourceKey && !entriesBySourceKey.has(sourceKey)) entriesBySourceKey.set(sourceKey, entry);
        });
      });

      const ordered: FrameworkArgument[] = [];
      const emittedTargetKeys = new Set<string>();
      const pushEntry = (entry: ActionArgBuildEntry | undefined) => {
        if (!entry || emittedTargetKeys.has(entry.targetKey)) return;
        ordered.push(entry.arg);
        emittedTargetKeys.add(entry.targetKey);
      };

      getRawActionArgOrder(argValues).forEach((sourceKey) => pushEntry(entriesBySourceKey.get(sourceKey)));
      entries.forEach(pushEntry);
      return ordered;
    };

    const args: FrameworkArgument[] =
      actionLabel && actionArgs
        ? orderActionArgEntries([
            ...actionArgs.flatMap((arg, index): ActionArgBuildEntry[] => {
              const frameworkArg = arg as FrameworkArgument;
              const positionalKey = `arg${index}`;
              const sourceKeys = [frameworkArg.name, positionalKey];
              if (isSequenceTupleArg(frameworkArg)) {
                const rows = sequenceTupleArgValueFromArgValues(frameworkArg, argValues, coerceFrameworkArgValuePreservingExpressions);
                const rawValue = String(argValues[frameworkArg.name] ?? '').trim();
                const actionArg: FrameworkArgument = {
                  name: frameworkArg.name,
                  type: frameworkArg.type,
                  members: frameworkArg.members,
                  // No rows but a preserved unsupported expression => keep it verbatim.
                  value: rows.length === 0 && rawValue ? rawValue : rows,
                };
                return [{ targetKey: frameworkArg.name, sourceKeys, arg: actionArg }];
              }
              if (isTupleArg(frameworkArg)) {
                const rawSourceExpression = getRawActionArgValue(argValues, frameworkArg.name);
                const actionArg: FrameworkArgument = {
                  name: frameworkArg.name,
                  type: frameworkArg.type,
                  members: frameworkArg.members,
                  value: rawSourceExpression || tupleValueFromArgValues(frameworkArg, argValues, coerceFrameworkArgValuePreservingExpressions),
                  rawSourceExpression,
                };
                return [{ targetKey: frameworkArg.name, sourceKeys, arg: actionArg }];
              }
              const hasNamedValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, frameworkArg.name);
              const hasPositionalValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, positionalKey);
              const rawSourceExpression = getRawActionArgValue(argValues, hasNamedValue ? frameworkArg.name : positionalKey);
              const raw = String(hasNamedValue ? argValues[frameworkArg.name] ?? '' : argValues[positionalKey] ?? '');
              if (
                isHiddenRuntimeActionArg(frameworkArg) &&
                !hasNamedValue &&
                !hasPositionalValue &&
                rawSourceExpression === undefined &&
                raw.trim() === ''
              ) {
                return [];
              }
              const value =
                raw === '' && isFrameworkArgOptionalNoneDefault(frameworkArg)
                  ? null
                  : coerceFrameworkArgValuePreservingExpressions(frameworkArg, raw);
              const actionArg: FrameworkArgument = {
                name: frameworkArg.name,
                type: frameworkArg.type,
                value,
                rawSourceExpression,
              };
              return [{ targetKey: frameworkArg.name, sourceKeys, arg: actionArg }];
            }),
            ...Object.entries(argValues || {})
              .filter(([argName]) => {
                const normalized = String(argName || '').trim();
                return (
                  PYTHON_IDENTIFIER_RE.test(normalized) &&
                  !actionArgs.some((arg) => String(arg.name || '').trim() === normalized) &&
                  !/^arg\d+$/.test(normalized)
                );
              })
              .map(
                ([argName, rawValue]): ActionArgBuildEntry => ({
                  targetKey: argName,
                  sourceKeys: [argName],
                  arg: {
                    name: argName,
                    type: '',
                    value: rawValue,
                    rawSourceExpression: getRawActionArgValue(argValues, argName),
                  },
                })
              ),
          ])
        : [];
    return {
      parentName,
      parentClass: actionItem?.parentClass,
      mechanismName,
      mechanismClass: actionItem?.mechanismClass,
      action: { name: actionLabel ? actionName : '', arguments: args },
    };
  };

  const buildBindingsForDrafts = useCallback(
    (targetButton: string, targetDrafts: EditableBindingDraft[]): InputBinding[] => {
      const inferredReceiverVarName = inferSingleReceiverVarName(targetDrafts) ?? undefined;
      const nextBindings = targetDrafts
        .map((draft): InputBinding | null => {
          if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
            if (!String(draft.onActionLabel || '').trim() || !String(draft.offActionLabel || '').trim()) return null;
            const onActionForSave = serializableActionForSave(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel, draft.onArgValues);
            const offActionForSave = serializableActionForSave(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel, draft.offArgValues);
            return withInputBindingId({
              device,
              receiverVarName: draft.receiverVarName || inferredReceiverVarName,
              sourceLineNumber: draft.sourceLineNumber,
              button: targetButton,
              kind: INPUT_BINDING_KIND.TOGGLE,
              onAction: buildBindingAction(
                draft.onActionLabel,
                onActionForSave.actionDef,
                actionItemForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel),
                onActionForSave.argValues
              ),
              offAction: buildBindingAction(
                draft.offActionLabel,
                offActionForSave.actionDef,
                actionItemForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel),
                offActionForSave.argValues
              ),
            });
          }
          if (!String(draft.actionLabel || '').trim()) return null;
          const actionForSave = serializableActionForSave(draft.kind, draft.actionLabel, draft.argValues);
          return withInputBindingId({
            device,
            receiverVarName: draft.receiverVarName || inferredReceiverVarName,
            sourceLineNumber: draft.sourceLineNumber,
            button: targetButton,
            kind: draft.kind,
            action: buildBindingAction(
              draft.actionLabel,
              actionForSave.actionDef,
              actionItemForLabel(draft.kind, draft.actionLabel),
              actionForSave.argValues
            ),
          });
        })
        .filter((binding): binding is InputBinding => binding !== null);
      return dedupeInputBindingsBySignature(nextBindings);
    },
    [actionItemForLabel, device, serializableActionForSave]
  );

  const buildBindingsFromDrafts = useCallback((): InputBinding[] => {
    return buildBindingsForDrafts(button, drafts);
  }, [buildBindingsForDrafts, button, drafts]);

  const loadButtonDrafts = useCallback(
    (nextButton: string) => {
      const parsedDrafts = createDraftsForButton(nextButton);
      const semanticDrafts = parsedDrafts.map((draft) => syncArgValues(draft, 'semantic'));
      const nextDrafts = parsedDrafts.map((draft) => syncArgValues(draft, 'display'));
      setButton(nextButton);
      setDrafts(nextDrafts);
      setSaveError(null);
      setInitialEffectiveSnapshot(canonicalBindingSnapshot(buildBindingsForDrafts(nextButton, semanticDrafts)));
    },
    [buildBindingsForDrafts, createDraftsForButton, syncArgValues]
  );

  useEffect(() => {
    if (!open) return;
    setDrafts((prev) => {
      const next = prev.map((draft) => syncArgValues(draft, 'display'));
      return normalizedDraftSnapshot(prev) === normalizedDraftSnapshot(next) ? prev : next;
    });
  }, [open, syncArgValues]);

  useLayoutEffect(() => {
    if (!open) return;
    loadButtonDrafts(initialButton || defaultButtonKeys[0] || '');
    setPendingButton(null);
  }, [open, initialButton, defaultButtonKeys, loadButtonDrafts]);

  const canonicalDraftBindings = useMemo(() => buildBindingsFromDrafts(), [buildBindingsFromDrafts]);
  const currentEffectiveSnapshot = useMemo(() => canonicalBindingSnapshot(canonicalDraftBindings), [canonicalDraftBindings]);
  const isDirty = currentEffectiveSnapshot !== initialEffectiveSnapshot;

  const getSaveFailureMessage = useCallback(
    (reason: string | null) => {
      const normalizedReason = String(reason || '').toLowerCase();
      if (
        normalizedReason.includes('relocat') ||
        normalizedReason.includes('declaration') ||
        normalizedReason.includes('receiver') ||
        normalizedReason.includes('scope')
      ) {
        return t(
          'controller.buttonMapping.dialog.saveUnsafeRelocationError',
          'This mapping cannot be updated because it would need to move outside its original scope or safe declaration order. Move the required declaration earlier, delete the original mapping, or cancel without saving.'
        );
      }
      return t('controller.buttonMapping.dialog.saveError', 'Could not update this mapping. The program was not changed.');
    },
    [t]
  );

  const canSave = useMemo(() => {
    if (!button) return false;
    const scalarArgIsValid = (arg: FrameworkArgument, valueKey: string, argValues: Record<string, string>) =>
      scalarFrameworkArgIsValid(arg, valueKey, argValues, t);
    const actionIsValid = (kind: EditableBindingKind, actionLabel: string, argValues: Record<string, string>, required: boolean) => {
      if (!actionLabel) return !required;
      const actionDef = validatedActionDefForLabelAndValues(kind, actionLabel, argValues);
      const args = actionDef?.arguments || [];
      return args.every((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // Each row's members must be valid (e.g. line_number in range) and the bounded-integer
          // key member (e.g. line_number) must be unique across rows.
          return sequenceTupleRowsAreValid(frameworkArg, argValues, t);
        }
        if (isTupleArg(frameworkArg)) {
          if (getRawActionArgValue(argValues, frameworkArg.name)) return true;
          return (frameworkArg.members || []).every((member) =>
            scalarArgIsValid(member as FrameworkArgument, tupleMemberValueKey(frameworkArg.name, member.name), argValues)
          );
        }
        return scalarArgIsValid(frameworkArg, frameworkArg.name, argValues);
      });
    };
    return drafts.every((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        const hasAnyToggleAction = !!draft.onActionLabel || !!draft.offActionLabel;
        return (
          actionIsValid(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel, draft.onArgValues, hasAnyToggleAction) &&
          actionIsValid(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel, draft.offArgValues, hasAnyToggleAction)
        );
      }
      return actionIsValid(draft.kind, draft.actionLabel, draft.argValues, false);
    });
  }, [button, drafts, t, validatedActionDefForLabelAndValues]);

  const saveDrafts = (): boolean => {
    if (!canSave || !ready) return false;
    setSaveError(null);
    const saveResult = onSave(button, canonicalDraftBindings);
    if (buttonMappingSaveSucceeded(saveResult)) {
      setInitialEffectiveSnapshot(currentEffectiveSnapshot);
      return true;
    }
    setSaveError(getSaveFailureMessage(buttonMappingSaveFailureReason(saveResult)));
    return false;
  };

  const handleUpdate = () => {
    if (saveDrafts()) onClose();
  };

  const requestButtonChange = (event: SelectChangeEvent<string>) => {
    const nextButton = event.target.value;
    if (nextButton === button) return;
    if (isDirty) {
      setPendingButton(nextButton);
      return;
    }
    loadButtonDrafts(nextButton);
  };

  const discardAndSwitchButton = () => {
    if (!pendingButton) return;
    loadButtonDrafts(pendingButton);
    setPendingButton(null);
  };

  const updateAndSwitchButton = () => {
    if (!pendingButton) return;
    if (!saveDrafts()) return;
    loadButtonDrafts(pendingButton);
    setPendingButton(null);
  };

  const argumentLabelWidthCh = useMemo(() => {
    const allLabels = drafts.flatMap((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return [
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkArgLabel(t, arg.name)
          ) || []),
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkArgLabel(t, arg.name)
          ) || []),
        ];
      }
      return actionDefForLabel(draft.kind, draft.actionLabel)?.arguments?.map((arg) => getLocalizedFrameworkArgLabel(t, arg.name)) || [];
    });
    const longest = allLabels.reduce((max, label) => Math.max(max, label.length), 0);
    return Math.min(Math.max(longest + 1, 8), 18);
  }, [actionDefForLabel, drafts, t]);

  const argumentUnitWidthCh = useMemo(() => {
    const allUnits = drafts.flatMap((draft) => {
      if (draft.kind === INPUT_BINDING_KIND.TOGGLE) {
        return [
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.onActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
          ) || []),
          ...(actionDefForLabel(INPUT_BINDING_KIND.TOGGLE, draft.offActionLabel)?.arguments?.map((arg) =>
            getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
          ) || []),
        ];
      }
      return (
        actionDefForLabel(draft.kind, draft.actionLabel)?.arguments?.map((arg) =>
          getLocalizedFrameworkUnit(t, (arg as FrameworkArgument).units || '')
        ) || []
      );
    });
    const longest = allUnits.reduce((max, unit) => Math.max(max, unit.length), 0);
    if (longest === 0) return 0;
    return Math.min(Math.max(longest + 1, 6), 16);
  }, [actionDefForLabel, drafts, t]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        // Keep restore-focus deferred until after the exit transition clears aria-hidden.
        disableRestoreFocus
        slotProps={{ paper: { sx: { maxHeight: '82vh' } }, transition: { onExited: handleDialogExited } }}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1 }}>
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            {!ready && (
              <Alert severity="warning">
                {t(
                  'controller.buttonMapping.treeSitter.dialogUnavailable',
                  'Input mapping tools are unavailable. Cancel to keep browsing, then retry from the Control panel.'
                )}
              </Alert>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('controller.buttonMapping.dialog.buttonLabel', 'Button')}
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  size="small"
                  value={button}
                  onChange={requestButtonChange}
                  sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                >
                  {buttonOptions.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: 12 }}>
                      {labelForButtonOption(opt) || opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: '52vh' }}>
              <Stack spacing={1.25}>
                {drafts.map((draft) => {
                  const actionDef =
                    draft.kind === INPUT_BINDING_KIND.TOGGLE ? null : actionDefForLabelAndValues(draft.kind, draft.actionLabel, draft.argValues);
                  const actionArgs = actionDef?.arguments || [];
                  return (
                    <Box
                      key={draft.draftId}
                      data-testid="binding-draft"
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Box sx={{ flex: '0 0 128px' }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                size="small"
                                value={draft.kind}
                                onChange={(event) => handleKindChange(draft.draftId, event.target.value as EditableBindingKind)}
                                inputProps={{ 'aria-label': t('controller.buttonMapping.dialog.mappingTypeAriaLabel', 'Mapping type') }}
                                sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                              >
                                {allowedKinds.map((kind) => (
                                  <MenuItem key={kind} value={kind} sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                                    {bindingKindLabels[kind]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          {draft.kind !== INPUT_BINDING_KIND.TOGGLE && (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <ActionSelector
                                value={draft.actionLabel}
                                onChange={(value) => handleActionChange(draft.draftId, value)}
                                actions={draft.kind === INPUT_BINDING_KIND.VARIABLE ? globalAxisActionList : globalActionList}
                                customActions={draft.kind === INPUT_BINDING_KIND.VARIABLE ? [] : undefined}
                                allowNone
                                noneLabel={t('controller.buttonMapping.dialog.selectAction', 'Select action')}
                                ariaLabel={t('controller.buttonMapping.dialog.mappingActionAriaLabel', 'Mapping action')}
                              />
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <IconButton
                              size="small"
                              aria-label={t('controller.buttonMapping.dialog.removeMappingAriaLabel', 'Remove mapping')}
                              onClick={() => handleRemoveDraft(draft.draftId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Stack>

                        {draft.kind === INPUT_BINDING_KIND.TOGGLE && (
                          <Stack spacing={1.25}>
                            {(['on', 'off'] as const).map((slot) => {
                              const label = slot === 'on' ? draft.onActionLabel : draft.offActionLabel;
                              const argValues = slot === 'on' ? draft.onArgValues : draft.offArgValues;
                              const slotActionDef = actionDefForLabelAndValues(INPUT_BINDING_KIND.TOGGLE, label, argValues);
                              const slotArgs = slotActionDef?.arguments || [];
                              return (
                                <Box key={slot}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {slot === 'on'
                                      ? t('controller.buttonMapping.dialog.toggleOnLabel', 'Toggle on')
                                      : t('controller.buttonMapping.dialog.toggleOffLabel', 'Toggle off')}
                                  </Typography>
                                  <ActionSelector
                                    value={label}
                                    onChange={(value) => handleActionChange(draft.draftId, value, slot)}
                                    actions={globalActionList}
                                    allowNone
                                    noneLabel={
                                      slot === 'on'
                                        ? t('controller.buttonMapping.dialog.selectToggleOnAction', 'Select toggle on action')
                                        : t('controller.buttonMapping.dialog.selectToggleOffAction', 'Select toggle off action')
                                    }
                                    ariaLabel={
                                      slot === 'on'
                                        ? t('controller.buttonMapping.dialog.toggleOnActionAriaLabel', 'Toggle on action')
                                        : t('controller.buttonMapping.dialog.toggleOffActionAriaLabel', 'Toggle off action')
                                    }
                                  />
                                  {slotArgs.length > 0 && (
                                    <Box sx={{ mt: 1.25 }}>
                                      <FrameworkActionArgumentFields
                                        actionDef={slotActionDef}
                                        values={argValues}
                                        onArgChange={(name, value) =>
                                          updateDraft(draft.draftId, (current) => {
                                            if (current.kind !== INPUT_BINDING_KIND.TOGGLE) return current;
                                            return slot === 'on'
                                              ? { ...current, onArgValues: updateArgValuesForAction(slotActionDef, current.onArgValues, name, value) }
                                              : {
                                                  ...current,
                                                  offArgValues: updateArgValuesForAction(slotActionDef, current.offArgValues, name, value),
                                                };
                                          })
                                        }
                                        onArgValuesChange={(next) =>
                                          updateDraft(draft.draftId, (current) => {
                                            if (current.kind !== INPUT_BINDING_KIND.TOGGLE) return current;
                                            return slot === 'on' ? { ...current, onArgValues: next } : { ...current, offArgValues: next };
                                          })
                                        }
                                        pathOptions={pathOptions}
                                        enumOptions={FRAMEWORK_ENUM_OPTIONS}
                                        t={t}
                                        layout="row"
                                        showUnits
                                        labelWidthCh={argumentLabelWidthCh}
                                        unitWidthCh={argumentUnitWidthCh}
                                        spacing={1.5}
                                        idPrefix={`${draft.draftId}-${slot}`}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}

                        {draft.kind !== INPUT_BINDING_KIND.TOGGLE && actionArgs.length > 0 && (
                          <Box>
                            <FrameworkActionArgumentFields
                              actionDef={actionDef}
                              values={draft.argValues}
                              onArgChange={(name, value) =>
                                updateDraft(draft.draftId, (current) =>
                                  current.kind === INPUT_BINDING_KIND.TOGGLE
                                    ? current
                                    : {
                                        ...current,
                                        argValues: updateArgValuesForAction(actionDef, current.argValues, name, value),
                                      }
                                )
                              }
                              onArgValuesChange={(next) =>
                                updateDraft(draft.draftId, (current) =>
                                  current.kind === INPUT_BINDING_KIND.TOGGLE ? current : { ...current, argValues: next }
                                )
                              }
                              pathOptions={pathOptions}
                              enumOptions={FRAMEWORK_ENUM_OPTIONS}
                              t={t}
                              layout="row"
                              showUnits
                              labelWidthCh={argumentLabelWidthCh}
                              unitWidthCh={argumentUnitWidthCh}
                              spacing={1.5}
                              idPrefix={draft.draftId}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Button startIcon={<AddIcon />} onClick={handleAddDraft} size="small">
                {t('controller.buttonMapping.dialog.addMapping', 'Add mapping')}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('controller.buttonMapping.dialog.cancel', 'Cancel')}</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={!canSave || !isDirty || !ready}>
            {t('controller.buttonMapping.dialog.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingButton !== null} onClose={() => setPendingButton(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('controller.buttonMapping.dialog.unsavedTitle', 'Unsaved mapping changes')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('controller.buttonMapping.dialog.unsavedBody', 'Update this button before switching, or discard the current draft changes.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingButton(null)}>{t('controller.buttonMapping.dialog.cancel', 'Cancel')}</Button>
          <Button onClick={discardAndSwitchButton}>{t('controller.buttonMapping.dialog.discardChanges', 'Discard changes')}</Button>
          <Button onClick={updateAndSwitchButton} variant="contained" disabled={!canSave || !ready}>
            {t('controller.buttonMapping.dialog.update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ButtonMappingDialog;
